/**
 * LaGift — variant picker
 * ---------------------------------------------------------------------------
 * Reads the product's variants from a JSON script beside it and resolves the
 * current selection locally. No request per click, and nothing coupled to a
 * section template.
 *
 * It does not update the page itself. It announces the variant it resolved and
 * the section that owns the form decides what that means - which is what lets
 * the same picker serve the featured product section today and the product page
 * later.
 */
/**
 * Scoped so the file can run more than once.
 *
 * A `<script src>` executes once per tag, not once per URL. Several sections
 * include this, and a merchant can add the same section twice, so on a busy
 * page it runs repeatedly - and a top-level class or const throws
 * "Identifier ... has already been declared" on every run after the first.
 * Inside a function there is nothing shared to collide with.
 */
(() => {
  class LagiftVariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variant-data]');
      if (!data) return;

      try {
        this.variants = JSON.parse(data.textContent);
      } catch {
        // A malformed payload should leave a picker that does nothing, not a page
        // with a broken script.
        return;
      }

      this.addEventListener('change', () => this.onChange());

      // Announce the starting state, so whatever is listening does not have to
      // duplicate the resolution logic just to render the first paint.
      //
      // Deferred by a turn of the event loop on purpose. The picker sits INSIDE
      // the section that listens to it, so its closing tag - and therefore its
      // upgrade - comes first: announcing synchronously here fires the event
      // before the section has attached its listener, and the section silently
      // misses it. Nothing depends on this arriving in the same task.
      setTimeout(() => this.announce(this.resolve()), 0);
    }

    /** The value chosen in each option group, in option order. */
    get selection() {
      return [...this.querySelectorAll('.lagift-variants__input:checked')]
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((input) => input.value);
    }

    resolve() {
      const chosen = this.selection;
      return this.variants.find(
        (variant) => variant.options.length === chosen.length && variant.options.every((value, i) => value === chosen[i])
      );
    }

    onChange() {
      this.announce(this.resolve());
    }

    announce(variant) {
      this.current = variant;

      // Values that cannot be reached from the rest of the current selection are
      // marked rather than removed. Removing them makes the row of sizes jump
      // about as choices are made, and hides that the product has that size at
      // all - which is information worth keeping.
      this.markUnavailable();

      this.dispatchEvent(
        new CustomEvent('lagift:variant:change', {
          bubbles: true,
          detail: { variant: variant || null },
        })
      );
    }

    markUnavailable() {
      const chosen = this.selection;

      this.querySelectorAll('.lagift-variants__input').forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;

        // What would be selected if this one input were chosen and everything
        // else left alone.
        const candidate = [...chosen];
        candidate[position] = input.value;

        const match = this.variants.find(
          (variant) => variant.options.length === candidate.length && variant.options.every((v, i) => v === candidate[i])
        );

        input.classList.toggle('is-unavailable', !match || !match.available);
      });
    }
  }

  if (!customElements.get('lagift-variant-picker')) {
    customElements.define('lagift-variant-picker', LagiftVariantPicker);
  }
})();
